import { Directive, ElementRef, OnDestroy, AfterViewInit, Input, Inject } from '@angular/core'
import { RouterService } from 'src/app/singleton-services/router.service'
import { NavigationEnd } from '@angular/router'
import { filter, withLatestFrom, startWith, delay, take } from 'rxjs/operators'
import { ReplaySubject, combineLatest, SchedulerLike } from 'rxjs'
import { RXJS_SCHEDULER } from 'src/app/app.config'

/* polyfill to support `ScrollIntoViewOptions` in Safari
 * https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView
 */
import scrollIntoView from 'smooth-scroll-into-view-if-needed'
import { WINDOW } from 'src/app/app.injections'
import { HttpStabilityService } from '../../common/http-stability.service'

interface ScrollToElementOnNavigationProps {
  scrollIntoViewOptions?: ScrollIntoViewOptions
  times?: number
  visibilityThreshold?: number
}

const defaultScrollIntoViewOptions = {
  behavior: 'smooth' as 'smooth',
  block: 'center' as 'center',
  inline: 'center' as 'center'
}

@Directive({
  selector: '[floScrollToElementOnNavigation]'
})
export class ScrollToElementOnNavigationDirective implements OnDestroy, AfterViewInit {
  private readonly nativeElement$ = new ReplaySubject<HTMLElement>(1)
  private readonly window$ = new ReplaySubject<Window>(1)
  private readonly ngOnDestroy$ = new ReplaySubject<void>(1)
  private readonly ngAfterViewInit$ = new ReplaySubject<void>(1)
  private readonly props$ = new ReplaySubject<ScrollToElementOnNavigationProps>(1)

  constructor(
    private readonly elementRef: ElementRef,
    private readonly routerService: RouterService,
    private readonly httpStabilityService: HttpStabilityService,
    @Inject(WINDOW) private readonly windowService: Window,
    @Inject(RXJS_SCHEDULER) private readonly scheduler: SchedulerLike
  ) {
    this.props$.next({
      scrollIntoViewOptions: defaultScrollIntoViewOptions,
      visibilityThreshold: 0
    })
  }

  @Input('floScrollToElementOnNavigation')
  set props(props: ScrollToElementOnNavigationProps) {
    if (!!props) {
      this.props$.next(props)
    }
  }

  private readonly httpReady$ = this.httpStabilityService.hasOutstandingHttpRequests$.pipe(
    filter(hasOutstandingHttpRequests => !hasOutstandingHttpRequests),
    delay(10, this.scheduler)
  )

  private readonly routingComplete$ = this.routerService.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    startWith(true)
  )

  // tslint:disable-next-line:no-unused-variable
  private readonly scrollIntoView = combineLatest([
    this.routingComplete$,
    this.nativeElement$,
    this.ngAfterViewInit$,
    this.httpReady$
  ])
    .pipe(
      withLatestFrom(this.props$, this.window$),
      filter(([[event, nativeElement]]) => !!(nativeElement && nativeElement.getBoundingClientRect)),
      take(1)
    )
    .subscribe({
      next: ([[event, nativeElement], props]) => {
        // setting this to manual will disable the feature in the browser that keeps track of your
        // scroll position on the page that then autoscrolls you there when you come back
        if (history.scrollRestoration) {
          history.scrollRestoration = 'manual'
        }
        scrollIntoView(nativeElement, props.scrollIntoViewOptions as ScrollOptions)
      }
    })

  public ngAfterViewInit(): void {
    this.nativeElement$.next(this.elementRef.nativeElement)
    this.window$.next(this.windowService.window)
    this.ngAfterViewInit$.next()
    this.ngAfterViewInit$.complete()
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }
}
